import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { Footer } from "@/components/site/Footer";

const LegalLayout = ({ title, updated, children }) => {
  const SITE = useContent();
  return (
    <div className="bg-white min-h-screen">
      <header className="border-b border-rose-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" className="font-display text-2xl text-stone-900">
            {SITE.name}<span className="text-rose-700">.</span>
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-900">
            <ArrowLeft className="w-4 h-4" /> Início
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.25em] text-rose-800">Informação legal</p>
        <h1 className="font-display font-light text-4xl sm:text-5xl text-stone-900 mt-4">{title}</h1>
        <p className="text-sm text-stone-400 mt-3">Última atualização: {updated}</p>
        <div className="legal-prose mt-10 space-y-6 text-stone-600 leading-relaxed">{children}</div>
      </main>
      <Footer />
    </div>
  );
};

const H2 = ({ children }) => (
  <h2 className="font-display text-2xl text-stone-900 pt-4">{children}</h2>
);

export function PrivacyPolicy() {
  const SITE = useContent();
  return (
    <LegalLayout title="Política de Privacidade" updated="junho de 2026">
      <p>
        A presente Política de Privacidade descreve como são tratados os dados pessoais recolhidos
        através deste website, no âmbito dos serviços de psicologia clínica prestados por {SITE.fullName}.
        O tratamento de dados respeita o Regulamento Geral sobre a Proteção de Dados (RGPD —
        Regulamento (UE) 2016/679) e a legislação portuguesa aplicável.
      </p>

      <H2>1. Responsável pelo tratamento</H2>
      <p>
        {SITE.fullName}, psicóloga clínica, é a responsável pelo tratamento dos dados pessoais.
        Para qualquer questão relacionada com privacidade pode contactar através do email{" "}
        <a href={`mailto:${SITE.email}`} className="text-rose-800 underline">{SITE.email}</a>.
      </p>

      <H2>2. Que dados recolhemos</H2>
      <p>Recolhemos apenas os dados necessários para a marcação e prestação das consultas:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Nome;</li>
        <li>Endereço de email;</li>
        <li>Número de telefone;</li>
        <li>Data e hora pretendidas para a consulta;</li>
        <li>Mensagem ou informação que opte por partilhar no formulário de marcação;</li>
        <li>Testemunhos que submeta voluntariamente (nome e texto).</li>
      </ul>

      <H2>3. Finalidades e fundamento legal</H2>
      <p>Os dados são tratados para:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Gerir e confirmar as marcações de consulta (execução de um contrato/diligências pré-contratuais);</li>
        <li>Contactar o/a utente sobre a sua marcação (interesse legítimo e consentimento);</li>
        <li>Cumprir obrigações legais aplicáveis à atividade clínica;</li>
        <li>Publicar testemunhos, apenas após o seu consentimento e aprovação.</li>
      </ul>

      <H2>4. Prazo de conservação</H2>
      <p>
        Os dados são conservados apenas pelo período necessário às finalidades indicadas e para
        cumprimento das obrigações legais de conservação de registos clínicos. Findo esse prazo,
        os dados são eliminados ou anonimizados.
      </p>

      <H2>5. Partilha de dados</H2>
      <p>
        Os seus dados não são vendidos nem cedidos a terceiros para fins de marketing. Poderão ser
        tratados por prestadores de serviços que apoiam o funcionamento do website (alojamento e
        envio de emails de confirmação), que atuam como subcontratantes e estão obrigados à
        confidencialidade e segurança dos dados.
      </p>

      <H2>6. Cookies e marketing (Google Ads)</H2>
      <p>
        Mediante o seu consentimento, este website utiliza o Google Ads para medir a eficácia das
        campanhas publicitárias (acompanhamento de conversões), através do serviço Google. Utilizamos
        o Consent Mode v2 do Google: por defeito, os cookies de estatística e de marketing estão
        desativados e só são ativados depois de aceitar na faixa de cookies. Este acompanhamento
        <strong> não inclui</strong> o seu nome, email, telefone ou o conteúdo das mensagens — apenas
        regista eventos como "marcação concluída" (com o identificador interno e o valor da consulta)
        ou "clique no telefone". Pode gerir a sua escolha conforme descrito na{" "}
        <Link to="/politica-de-cookies" className="text-rose-800 underline">Política de Cookies</Link>.
      </p>

      <H2>7. Sigilo profissional</H2>
      <p>
        Toda a informação partilhada em contexto de consulta está protegida pelo sigilo profissional
        e pelo Código Deontológico da Ordem dos Psicólogos Portugueses.
      </p>

      <H2>8. Os seus direitos</H2>
      <p>
        Tem o direito de aceder, retificar, apagar, limitar ou opor-se ao tratamento dos seus dados,
        bem como o direito à portabilidade e à retirada do consentimento a qualquer momento. Para
        exercer estes direitos, contacte{" "}
        <a href={`mailto:${SITE.email}`} className="text-rose-800 underline">{SITE.email}</a>.
        Tem ainda o direito de apresentar reclamação à autoridade de controlo, a Comissão Nacional
        de Proteção de Dados (CNPD — <a href="https://www.cnpd.pt" target="_blank" rel="noreferrer" className="text-rose-800 underline">www.cnpd.pt</a>).
      </p>

      <H2>9. Segurança</H2>
      <p>
        São adotadas medidas técnicas e organizativas adequadas para proteger os dados pessoais
        contra acesso, perda ou divulgação não autorizados.
      </p>

      <H2>10. Alterações</H2>
      <p>
        Esta política pode ser atualizada. A data da última atualização encontra-se indicada no topo
        desta página.
      </p>
    </LegalLayout>
  );
}

export function CookiePolicy() {
  const SITE = useContent();
  return (
    <LegalLayout title="Política de Cookies" updated="junho de 2026">
      <p>
        Esta Política de Cookies explica o que são cookies e como são utilizados neste website de
        {" "}{SITE.fullName}.
      </p>

      <H2>1. O que são cookies</H2>
      <p>
        Cookies são pequenos ficheiros de texto guardados no seu dispositivo quando visita um
        website. Servem para permitir o funcionamento do site e melhorar a sua experiência.
      </p>

      <H2>2. Cookies que utilizamos</H2>
      <p>Este website utiliza as seguintes categorias de cookies:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Essenciais</strong> — necessários ao funcionamento do site. Inclui o cookie de
          autenticação da área reservada (acesso da psicóloga à gestão de marcações) e o registo da
          sua preferência de consentimento. Estão sempre ativos.
        </li>
        <li>
          <strong>Estatísticas</strong> — ativados apenas com o seu consentimento, ajudam a perceber
          como o site é utilizado.
        </li>
        <li>
          <strong>Marketing (Google Ads)</strong> — ativados apenas com o seu consentimento. São
          utilizados para medir a eficácia dos anúncios do Google Ads (acompanhamento de conversões,
          como uma marcação concluída ou um clique no telefone). Enquanto não consentir, estes
          cookies permanecem desativados (Consent Mode v2, por defeito negado).
        </li>
      </ul>
      <p>
        Não utilizamos os seus dados para perfilização invasiva. Os cookies de marketing só são
        ativados após o seu consentimento explícito e nunca partilham o nome, email, telefone ou
        mensagens das consultas.
      </p>

      <H2>3. Gestão do consentimento</H2>
      <p>
        No primeiro acesso ao site pode escolher "Aceitar todos", "Recusar" ou "Personalizar" as
        categorias de cookies. A sua escolha é guardada no seu dispositivo. Pode ainda, a qualquer
        momento, bloquear ou eliminar os cookies através das definições do seu navegador. A
        desativação de cookies essenciais pode afetar algumas funcionalidades.
      </p>

      <H2>4. Contacto</H2>
      <p>
        Para esclarecimentos sobre esta política, contacte{" "}
        <a href={`mailto:${SITE.email}`} className="text-rose-800 underline">{SITE.email}</a>.
      </p>
    </LegalLayout>
  );
}